import React, { useState, useEffect, useRef } from "react";
import NavBar from "../components/NavBar";

const ContactPage = () => {
  // State management
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const formResetTimeoutRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  // Animation states
  const [animate, setAnimate] = useState(false);

  // Brand colors
  const colors = {
    brown: "#A3582A",
    green: "#8C8660",
    beige: "#EFE5C6",
    yellow: "#E9BA67",
    lightGreen: "#E4E2D5",
    gray: "#5E6065",
  };

  // Handle scroll animations
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
      if (window.scrollY > 100) {
        setAnimate(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    const introTimer = setTimeout(() => setAnimate(true), 500);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(introTimer);
    };
  }, []);

  useEffect(
    () => () => {
      if (formResetTimeoutRef.current) {
        clearTimeout(formResetTimeoutRef.current);
      }
    },
    []
  );

  // Form handling
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formResetTimeoutRef.current) {
      clearTimeout(formResetTimeoutRef.current);
    }
    setIsFormSubmitted(true);
    formResetTimeoutRef.current = setTimeout(() => {
      setIsFormSubmitted(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
      formResetTimeoutRef.current = null;
    }, 5000);
  };

  // Parallax effect calculation - reduced effect on mobile
  const calculateParallax = (factor) => {
    // Less pronounced parallax on smaller screens
    const reducedFactor = window.innerWidth < 768 ? factor * 0.5 : factor;
    return scrollPosition * reducedFactor;
  };

  return (
    <>
      <NavBar />
      
      {/* Hero Section - Responsive */}
      <div className="relative py-10 sm:py-14 md:py-20 overflow-hidden">
        {/* Luxurious Background Elements */}
        <div className="absolute inset-0 bg-white z-0"></div>

        {/* Soft Glow Background - Responsive sizing */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] sm:w-[450px] sm:h-[450px] md:w-[600px] md:h-[600px] bg-[#E9BA67]/10 rounded-full blur-3xl pointer-events-none"></div>

        {/* Background Texture Overlay */}
        <div
          className="absolute inset-0 z-5 opacity-5 mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23a3582a' fill-opacity='0.4' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3Ccircle cx='13' cy='13' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: "8px 8px",
          }}
        ></div>

        {/* Subtle Diagonal Line Accents - Hidden on mobile */}
        <div className="absolute top-0 left-1/4 h-full w-px bg-gradient-to-b from-transparent via-[#A3582A]/10 to-transparent hidden sm:block"></div>
        <div className="absolute top-0 right-1/4 h-full w-px bg-gradient-to-b from-transparent via-[#E9BA67]/15 to-transparent hidden sm:block"></div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center reveal-item">
            <div className="inline-block px-4 py-1.5 sm:px-6 sm:py-2 rounded-full mb-4 sm:mb-8 bg-white/80 backdrop-blur-md border border-[#A3582A]/20 shadow-md">
              <p
                className="font-futura text-xs sm:text-sm flex items-center"
                style={{ color: colors.brown }}
              >
                <span
                  className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full mr-1.5 sm:mr-2 pulse-animation"
                  style={{ backgroundColor: colors.brown }}
                ></span>
                Experience Takeaya
              </p>
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-4 sm:mb-6 tracking-tight staggered-title font-ivyMode" style={{ color: "#A3582A" }}>
              <span className="block reveal-heading">Connect With Our</span>
              <span className="relative inline-block reveal-heading delay-200">
                <span className="absolute -rotate-2 inset-0 rounded-md heading-background" style={{ backgroundColor: 'rgba(233, 186, 103, 0.2)' }}></span>
                <span className="relative" style={{ color: "#A3582A" }}>Luxury Specialists</span>
              </span>
            </h2>

            <p
              className="text-sm sm:text-base md:text-lg lg:text-xl max-w-3xl mx-auto reveal-paragraph font-futura"
              style={{ color: colors.gray }}
            >
              Begin a conversation with our dedicated team to discover how Takeaya can elevate
              your property experience or create your perfect luxury stay in Dubai.
            </p>
          </div>
        </div>
      </div>
      
      {/* Contact Form Section - Responsive */}
      <div className="relative z-20 pb-12 sm:pb-16 md:pb-24 -mt-4 sm:-mt-10">
        {/* Abstract Luxury Background Shapes - Hidden on smallest screens */}
        <div className="absolute inset-0 -z-10 opacity-20 pointer-events-none hidden sm:block">
          {/* Floating circles - Responsive sizing */}
          <div
            className="absolute top-[10%] left-[5%] w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 rounded-full"
            style={{
              background: `radial-gradient(circle, ${colors.yellow}50, transparent 70%)`,
              transform: `translate(${calculateParallax(-0.05)}px, ${calculateParallax(0.02)}px)`,
            }}
          ></div>
          <div
            className="absolute top-[40%] right-[10%] w-48 sm:w-64 md:w-96 h-48 sm:h-64 md:h-96 rounded-full"
            style={{
              background: `radial-gradient(circle, ${colors.brown}30, transparent 70%)`,
              transform: `translate(${calculateParallax(0.04)}px, ${calculateParallax(-0.03)}px)`,
            }}
          ></div>
          <div
            className="absolute bottom-[15%] left-[20%] w-40 sm:w-64 md:w-80 h-40 sm:h-64 md:h-80 rounded-full"
            style={{
              background: `radial-gradient(circle, ${colors.green}20, transparent 60%)`,
              transform: `translate(${calculateParallax(-0.03)}px, ${calculateParallax(0.04)}px)`,
            }}
          ></div>
        </div>
        
        <div className="w-full px-0 relative z-10">
          <div className="w-full mx-auto">
            <div 
              className={`bg-white shadow-lg sm:shadow-xl overflow-hidden border-y border-[#E9BA67]/10 transition-all duration-1000 ${animate ? 'translate-y-0' : 'translate-y-12'}`}
            >

          
              
              {/* Form and Contact Info Grid - Responsive layout */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 text-start">
                {/* Contact Information - Left Column stacks on mobile */}
                <div className="lg:col-span-3 bg-gradient-to-br from-[#A3582A] to-[#8C8660] p-6 sm:p-8 md:p-10 text-white relative overflow-hidden">

                  {/* Decorative Elements - Sized for mobile */}
                  <div className="absolute top-0 right-0 w-24 sm:w-40 h-24 sm:h-40 rounded-full opacity-20 bg-white"></div>
                  <div className="absolute bottom-0 left-0 w-24 sm:w-40 h-24 sm:h-40 rounded-full opacity-10 bg-white"></div>
                  
                  <div className="relative z-10">
                    <h3 className="text-xl sm:text-2xl font-light mb-6 sm:mb-8 font-ivyMode">Contact Information</h3>
                    
                    <div className="space-y-4 sm:space-y-6">
                      {/* Address */}
                      <div className="flex items-start">
                        <div className="mt-1 mr-3 sm:mr-4">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm font-medium opacity-80 uppercase tracking-wider mb-1">Visit Us</p>
                          <p className="font-futura text-xs sm:text-sm opacity-90">
                            Downtown Dubai,<br />United Arab Emirates
                            DAMAC Smart Height,<br /> Barsha Height, Dubai
                          </p>
                        </div>
                      </div>
                      
                      {/* Phone */}
                      <div className="flex items-start">
                        <div className="mt-1 mr-3 sm:mr-4">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1.02 1.02 0 00-1.02.24l-2.2 2.2a15.045 15.045 0 01-6.59-6.59l2.2-2.21a.96.96 0 00.25-1A11.36 11.36 0 018.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm font-medium opacity-80 uppercase tracking-wider mb-1">Call Us</p>
                          <a
                            href="tel:+971588106718"
                            className="font-futura text-xs sm:text-sm opacity-90 hover:underline focus:outline-none focus:ring-2 focus:ring-white/60 rounded"
                          >
                            +971 58 810 6718
                          </a>
                        </div>
                      </div>
                      
                      {/* Email */}
                      <div className="flex items-start">
                        <div className="mt-1 mr-3 sm:mr-4">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm font-medium opacity-80 uppercase tracking-wider mb-1">Email Us</p>
                          <a
                            href="mailto:info@takeaya.com"
                            className="font-futura text-xs sm:text-sm opacity-90 hover:underline break-all focus:outline-none focus:ring-2 focus:ring-white/60 rounded"
                          >
                            info@takeaya.com
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Contact Form - Right Column */}
                <div className="lg:col-span-9 p-6 sm:p-8 md:p-10 text-start">
                  <h3 className="text-2xl sm:text-3xl font-light mb-4 sm:mb-6 font-ivyMode" style={{ color: colors.brown }}>
                    Let's Begin a Conversation
                  </h3>
                  
                  <p className="mb-6 sm:mb-8 font-futura text-sm sm:text-base" style={{ color: colors.gray }}>
                    Connect with us to explore how Takeaya can elevate your property experience or help you find your next extraordinary stay in Dubai.
                  </p>

                  {isFormSubmitted ? (
                    <div
                      className="mb-6 sm:mb-8 rounded-md border border-[#8C8660]/40 bg-[#E4E2D5]/50 px-4 py-3 text-sm font-futura text-[#5E6065]"
                      role="status"
                      aria-live="polite"
                    >
                      Thank you — your message has been received. Our team will get back to you shortly.
                    </div>
                  ) : null}
                  
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" aria-busy={isFormSubmitted}>
                    {/* Name and Email Fields - Stack on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div className="form-floating-label">
                        <input
                          type="text"
                          id="name"
                          name="name"
                          placeholder="Full Name"
                          value={formData.name}
                          onChange={handleChange}
                          disabled={isFormSubmitted}
                          className="w-full px-3 sm:px-4 py-3 sm:py-4 pt-5 sm:pt-6 rounded-md bg-[#EFE5C6]/10 border border-[#E9BA67]/30 focus:outline-none focus:ring-2 focus:ring-[#A3582A]/50 transition-all duration-300 peer text-sm sm:text-base disabled:opacity-60"
                          
                          required
                        />
                        <label
                          htmlFor="name"
                          className="absolute text-xs sm:text-sm text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-3 sm:left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3"
                        >
                          
                        </label>
                      </div>
                      
                      <div className="form-floating-label">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="Email Address"
                          value={formData.email}
                          onChange={handleChange}
                          disabled={isFormSubmitted}
                          className="w-full px-3 sm:px-4 py-3 sm:py-4 pt-5 sm:pt-6 rounded-md bg-[#EFE5C6]/10 border border-[#E9BA67]/30 focus:outline-none focus:ring-2 focus:ring-[#A3582A]/50 transition-all duration-300 peer text-sm sm:text-base disabled:opacity-60"
                          
                          required
                        />
                        <label
                          htmlFor="email"
                          className="absolute text-xs sm:text-sm text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-3 sm:left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3"
                        >
                          
                        </label>
                      </div>
                    </div>
                    
                    {/* Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div className="form-floating-label">
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleChange}
                          disabled={isFormSubmitted}
                          className="w-full px-3 sm:px-4 py-3 sm:py-4 pt-5 sm:pt-6 rounded-md bg-[#EFE5C6]/10 border border-[#E9BA67]/30 focus:outline-none focus:ring-2 focus:ring-[#A3582A]/50 transition-all duration-300 peer text-sm sm:text-base disabled:opacity-60"
                          
                        />
                        <label
                          htmlFor="phone"
                          className="absolute text-xs sm:text-sm text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-3 sm:left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3"
                        >
                          
                        </label>
                      </div>
                    </div>
                    
                    {/* Message Field - Responsive sizing */}
                    <div className="form-floating-label">
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Your Message"
                        disabled={isFormSubmitted}
                        className="w-full px-3 sm:px-4 py-3 sm:py-4 pt-5 sm:pt-6 rounded-md bg-[#EFE5C6]/10 border border-[#E9BA67]/30 focus:outline-none focus:ring-2 focus:ring-[#A3582A]/50 transition-all duration-300 peer min-h-[120px] sm:min-h-[150px] text-sm sm:text-base disabled:opacity-60"
                        
                        required
                      ></textarea>
                      <label
                        htmlFor="message"
                        className="absolute text-xs sm:text-sm text-gray-500 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] left-3 sm:left-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3"
                      >
                       
                      </label>
                    </div>
                    
                    {/* Submit Button - Responsive sizing */}
                    <div className="pt-4 sm:pt-6">
                      <button
                        type="submit"
                        disabled={isFormSubmitted}
                        className="group relative overflow-hidden px-6 sm:px-10 py-3 sm:py-4 rounded-full text-xs sm:text-sm font-medium shadow-md sm:shadow-lg w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <span className="absolute inset-0 bg-gradient-to-r from-[#A3582A] to-[#E9BA67] z-0"></span>
                        <span className="absolute inset-0 bg-gradient-to-r from-[#E9BA67] to-[#A3582A] z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
                        <span className="relative z-20 text-white uppercase tracking-wider flex items-center justify-center">
                          Send Message
                          <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 ml-1.5 sm:ml-2 transform group-hover:translate-x-1 transition-transform duration-300"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M14 5l7 7m0 0l-7 7m7-7H3"
                            />
                          </svg>
                        </span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CSS Animations - Modified for responsiveness */}
      <style>{`
        /* Floating Elements Animation - Smaller on mobile */}
        .floating-element {
          animation: float 6s ease-in-out infinite;
        }

        .floating-element-slow {
          animation: float 8s ease-in-out infinite;
        }

        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        
        /* Larger float on desktop */
        @media (min-width: 640px) {
          @keyframes float {
            0% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
            100% {
              transform: translateY(0px);
            }
          }
        }

        /* Pulse Animation - Smaller on mobile */}
        .pulse-animation {
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(163, 88, 42, 0.7);
          }

          70% {
            transform: scale(1);
            box-shadow: 0 0 0 3px rgba(163, 88, 42, 0);
          }

          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(163, 88, 42, 0);
          }
        }
        
        /* Larger pulse on desktop */
        @media (min-width: 640px) {
          @keyframes pulse {
            0% {
              transform: scale(0.95);
              box-shadow: 0 0 0 0 rgba(163, 88, 42, 0.7);
            }

            70% {
              transform: scale(1);
              box-shadow: 0 0 0 6px rgba(163, 88, 42, 0);
            }

            100% {
              transform: scale(0.95);
              box-shadow: 0 0 0 0 rgba(163, 88, 42, 0);
            }
          }
        }

        /* Form Floating Label - Responsive position */}
        .form-floating-label {
          position: relative;
        }
        
        /* Reveal Animations - Faster on mobile */}
        .reveal-item {
          opacity: 0;
          animation: revealUp 0.6s ease-out forwards;
        }
        
        /* Slower animations on desktop */}
        @media (min-width: 640px) {
          .reveal-item {
            animation-duration: 0.8s;
          }
        }

        .reveal-heading {
          opacity: 0;
          animation: revealLeft 0.8s ease-out forwards;
        }

        .delay-200 {
          animation-delay: 0.2s;
        }

        .reveal-paragraph {
          opacity: 0;
          animation: revealUp 0.8s ease-out forwards 0.4s;
        }

        @keyframes revealUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes revealLeft {
          from {
            transform: translateX(-30px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        /* Heading Background Animation */
        .heading-background {
          animation: drawBackground 0.5s ease-out forwards 0.3s;
          width: 0;
        }

        @keyframes drawBackground {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default ContactPage;